import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Divider,
  Avatar,
  Stack,
  InputAdornment,
  Autocomplete,
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Save,
  Refresh,
  AccountBalance,
  Person,
  Euro,
  CalendarToday,
  Business,
  CheckCircle,
  ArrowForward,
  InfoOutlined,
  Add,
} from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import UserService from '../../services/userService';
import UserDepositAccountService from '../../services/userDepositAccountService';
import LimitService from '../../services/limitService';
import useRoleColors from '../../hooks/useRoleColors';

const DefineLimit = ({ user }) => {
  const {
    accent,
    accentLight,
    accentDark,
    accentGradient,
    accentGradientHover,
    softBg,
    softBgHover,
    softBgStrong,
    contrast,
    outline,
  } = useRoleColors();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    userId: '',
    userName: '',
    amount: '',
    currency: 'EUR',
    depositAccountNumber: '',
    validityDate: null,
  });
  const [depositAccounts, setDepositAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [allUsers, setAllUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [userInputValue, setUserInputValue] = useState('');

  // Fetch all users for autocomplete (excluding current user)
  useEffect(() => {
    const fetchAllUsers = async () => {
      setLoadingUsers(true);
      try {
        const result = await UserService.getAllUsers(1, 1000);
        if (result.success && result.data) {
          // Format users for autocomplete: { userId, userName, email }
          // Exclude current user from the list
          const currentUserId = user?.userId;
          const formattedUsers = result.data
            .filter(u => u.userId !== currentUserId) // Exclude current user
            .map(u => ({
              userId: u.userId,
              userName: `${u.name || ''} ${u.surname || ''}`.trim() || u.email || `User #${u.userId}`,
              email: u.email || ''
            }));
          setAllUsers(formattedUsers);
        }
      } catch (error) {
        console.error('Error fetching users:', error);
      } finally {
        setLoadingUsers(false);
      }
    };
    fetchAllUsers();
  }, [user]);

  // Get customer name by ID
  const fetchCustomerName = async (userId) => {
    if (!userId) {
      setFormData(prev => ({ ...prev, userName: '' }));
      return;
    }

    // Check if user is trying to enter their own ID
    const currentUserId = user?.userId;
    if (userId === currentUserId?.toString()) {
      setError('You cannot define a limit for yourself. Please enter a different user ID.');
      setFormData(prev => ({ ...prev, userId: '', userName: '' }));
      setUserInputValue('');
      return;
    }

    const result = await UserService.getCustomerNameByUserId(userId);
    
    if (result.success) {
      setFormData(prev => ({ ...prev, userName: result.data.customerName }));
      setError('');
    } else {
      setFormData(prev => ({ ...prev, userName: '' }));
      setError(result.message);
    }
  };

  // Auto-fill userId from query params
  useEffect(() => {
    const userIdFromQuery = searchParams.get('userId');
    if (userIdFromQuery && !formData.userId) {
      setFormData(prev => ({
        ...prev,
        userId: userIdFromQuery
      }));
      setUserInputValue(userIdFromQuery);
      // Fetch customer name
      fetchCustomerName(userIdFromQuery);
    }
  }, [searchParams]);

  // Sync userInputValue with formData.userId when formData changes externally
  useEffect(() => {
    if (formData.userId) {
      const userFromList = allUsers.find(u => u.userId.toString() === formData.userId);
      if (userFromList) {
        setUserInputValue(formData.userId);
      } else if (!userInputValue || userInputValue !== formData.userId) {
        setUserInputValue(formData.userId);
      }
    } else if (!formData.userId && userInputValue) {
      setUserInputValue('');
    }
  }, [formData.userId, allUsers]);

  // Handle form input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (error) setError('');
    if (success) setSuccess('');
  };

  // Get deposit accounts by customer ID
  const fetchDepositAccounts = async (userId) => {
    if (!userId) {
      setDepositAccounts([]);
      return;
    }

    const result = await UserDepositAccountService.getDepositAccountsByCustomerId(userId);
    
    if (result.success) {
      setDepositAccounts(result.data);
      setError('');
    } else {
      setDepositAccounts([]);
      if (result.message.includes('not found')) {
        setError('No deposit account found for this user. Click "Create Deposit Account" button to create a default account.');
      } else {
        setError(result.message);
      }
    }
  };

  // Create default deposit account
  const createDefaultDepositAccount = async (userId) => {
    const result = await UserDepositAccountService.createDefaultDepositAccount(userId);
    
    if (result.success) {
      // Refresh deposit accounts
      fetchDepositAccounts(userId);
      setError('');
    }
  };

  // Handle user selection from autocomplete
  const handleUserChange = (event, newValue, reason) => {
    if (reason === 'selectOption' && newValue && typeof newValue === 'object') {
      // Check if user is trying to select themselves
      const currentUserId = user?.userId;
      if (newValue.userId === currentUserId) {
        setError('You cannot define a limit for yourself. Please select a different user.');
        setUserInputValue('');
        setFormData(prev => ({
          ...prev,
          userId: '',
          userName: ''
        }));
        return;
      }
      
      // Selected from dropdown - set both userId and userName immediately
      const userIdStr = newValue.userId.toString();
      setFormData(prev => ({
        ...prev,
        userId: userIdStr,
        userName: newValue.userName
      }));
      setUserInputValue(userIdStr);
      setError('');
      // Fetch deposit accounts
      fetchDepositAccounts(parseInt(userIdStr));
    } else if (reason === 'clear') {
      // Cleared
      setFormData(prev => ({
        ...prev,
        userId: '',
        userName: ''
      }));
      setUserInputValue('');
      setDepositAccounts([]);
      setError('');
    }
  };

  // Debounce user ID fetch when user types
  useEffect(() => {
    if (!userInputValue || userInputValue.trim() === '') {
      setFormData(prev => {
        if (!prev.userId) {
          return { ...prev, userId: '', userName: '' };
        }
        return prev;
      });
      return;
    }

    const currentUserId = user?.userId;

    // If it's a number (likely ID)
    if (/^\d+$/.test(userInputValue.trim())) {
      const trimmedValue = userInputValue.trim();
      
      // Check if user is trying to enter their own ID
      if (trimmedValue === currentUserId?.toString()) {
        setError('You cannot define a limit for yourself. Please enter a different user ID.');
        setFormData(prev => ({ ...prev, userId: '', userName: '' }));
        return;
      }
      
      // Check if this ID is in allUsers
      const userFromList = allUsers.find(u => u.userId.toString() === trimmedValue);
      
      if (userFromList) {
        setFormData(prev => ({
          ...prev,
          userId: trimmedValue,
          userName: userFromList.userName
        }));
        fetchDepositAccounts(parseInt(trimmedValue));
        setError('');
        return;
      }
      
      // Not in allUsers, update userId and fetch from API
      setFormData(prev => ({ ...prev, userId: trimmedValue }));
      const timeoutId = setTimeout(() => {
        fetchCustomerName(trimmedValue);
        fetchDepositAccounts(trimmedValue);
      }, 500);
      return () => clearTimeout(timeoutId);
    } else {
      // If it's text, try to find in all users
      const found = allUsers.find(u => 
        u.userName.toLowerCase() === userInputValue.toLowerCase() ||
        u.email.toLowerCase() === userInputValue.toLowerCase()
      );
      if (found) {
        setFormData(prev => ({
          ...prev,
          userId: found.userId.toString(),
          userName: found.userName
        }));
        setUserInputValue(found.userId.toString());
        fetchDepositAccounts(found.userId);
        setError('');
      } else {
        setFormData(prev => ({ ...prev, userId: userInputValue, userName: '' }));
      }
    }
  }, [userInputValue, allUsers, user]);

  // Format currency input
  const formatCurrencyInput = (value) => {
    // Remove all non-numeric characters except decimal point
    const numericValue = value.replace(/[^\d.]/g, '');
    
    // Ensure only one decimal point
    const parts = numericValue.split('.');
    if (parts.length > 2) {
      return parts[0] + '.' + parts.slice(1).join('');
    }
    
    // Limit decimal places to 2
    if (parts[1] && parts[1].length > 2) {
      parts[1] = parts[1].substring(0, 2);
    }
    
    return parts.join('.');
  };

  const handleAmountChange = (value) => {
    const formatted = formatCurrencyInput(value);
    handleInputChange('amount', formatted);
  };

  // Validate form
  const validateForm = () => {
    if (!formData.userId) {
      setError('Customer ID is required');
      return false;
    }
    if (!formData.userName) {
      setError('Please enter a valid Customer ID');
      return false;
    }
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      setError('Limit amount must be greater than zero');
      return false;
    }
    if (!formData.currency) {
      setError('Please select currency');
      return false;
    }
    if (!formData.depositAccountNumber) {
      setError('Please select deposit account number');
      return false;
    }
    if (!formData.validityDate) {
      setError('Validity date is required');
      return false;
    }
    const selectedDate = new Date(formData.validityDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (selectedDate <= today) {
      setError('Validity date must be a future date');
      return false;
    }
    return true;
  };

  // Submit form
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    setError('');
    setSuccess('');

    // Currency enum mapping
    const currencyMap = {
      'TRY': 1,
      'USD': 2,
      'EUR': 3,
      'GBP': 4
    };

    // Format date to YYYY-MM-DD for API
    const formattedDate = formData.validityDate instanceof Date 
      ? formData.validityDate.toISOString().split('T')[0]
      : formData.validityDate;

    const limitData = {
      userId: parseInt(formData.userId),
      amount: parseFloat(formData.amount),
      currency: currencyMap[formData.currency] || 3, // Default to EUR
      depositAccountNumber: parseInt(formData.depositAccountNumber),
      validityDate: formattedDate
    };
    
    const result = await LimitService.defineLimit(limitData);
    
    if (result.success) {
      setSuccess(result.message);
      
      // Reset form
      setFormData({
        userId: '',
        userName: '',
        amount: '',
        currency: 'EUR',
        depositAccountNumber: '',
        validityDate: null,
      });
      setDepositAccounts([]);
      setUserInputValue('');
    } else {
      setError(result.message);
    }
    
    setLoading(false);
  };

  // Reset form
  const handleReset = () => {
    setFormData({
      userId: '',
      userName: '',
      amount: '',
      currency: 'EUR',
      depositAccountNumber: '',
      validityDate: null,
    });
    setDepositAccounts([]);
    setUserInputValue('');
    setError('');
    setSuccess('');
  };

  return (
    <Box sx={{ 
      minHeight: '100vh',
      py: 4,
      px: { xs: 2, sm: 3 }
    }}>
      <Box sx={{ maxWidth: 1000, mx: 'auto' }}>
        
        {/* Main Card */}
        <Card elevation={24} sx={{ 
          borderRadius: 4,
          overflow: 'hidden'
        }}>
          
          {/* Header Banner */}
          <Box sx={{ 
            background: accentGradient,
            p: 3,
            color: 'white'
          }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item>
                <Avatar sx={{ 
                  width: 64, 
                  height: 64,
                  background: 'rgba(255,255,255,0.25)',
                  border: '3px solid rgba(255,255,255,0.3)'
                }}>
                  <AccountBalance sx={{ fontSize: 32 }} />
                </Avatar>
              </Grid>
              <Grid item xs>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Define Line of Credit
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Set new line of credit for customers
                </Typography>
              </Grid>
            </Grid>
          </Box>

          {/* Form */}
          <CardContent sx={{ p: 4 }}>
            <Box component="form" onSubmit={handleSubmit}>
              
              {/* Customer Information */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ 
                  mb: 3, 
                  display: 'flex', 
                  alignItems: 'center',
                  fontWeight: 600,
                  color: 'text.primary'
                }}>
                  <Person sx={{ mr: 1.5, color: accent }} />
                  Customer Information
                </Typography>
                
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <Autocomplete
                      freeSolo
                      options={allUsers}
                      fullWidth
                      getOptionLabel={(option) => {
                        if (!option) return '';
                        if (typeof option === 'string') return option;
                        return `${option.userName} (ID: ${option.userId})`;
                      }}
                      isOptionEqualToValue={(option, value) => {
                        if (typeof option === 'string' || typeof value === 'string') return false;
                        return option.userId === value.userId;
                      }}
                      value={
                        formData.userId 
                          ? (allUsers.find(u => u.userId.toString() === formData.userId) || null)
                          : null
                      }
                      inputValue={userInputValue}
                      onChange={handleUserChange}
                      onInputChange={(event, newInputValue, reason) => {
                        if (reason === 'input') {
                          setUserInputValue(newInputValue);
                        } else if (reason === 'reset') {
                          setUserInputValue('');
                        } else if (reason === 'clear') {
                          setUserInputValue('');
                        } else if (reason === 'selectOption') {
                          if (formData.userId) {
                            setUserInputValue(formData.userId);
                          }
                        }
                      }}
                      loading={loadingUsers}
                      sx={{ 
                        width: '100%',
                        display: 'block',
                        '& .MuiAutocomplete-root': {
                          width: '100%'
                        },
                        '& .MuiAutocomplete-inputRoot': {
                          width: '100% !important',
                          minWidth: '100% !important',
                          flexWrap: 'nowrap'
                        },
                        '& .MuiAutocomplete-input': {
                          width: '100% !important',
                          minWidth: 0,
                          flex: '1 1 auto'
                        }
                      }}
                      filterOptions={(options, { inputValue }) => {
                        if (!inputValue) return options;
                        const searchTerm = inputValue.toLowerCase();
                        return options.filter(option =>
                          option.userName.toLowerCase().includes(searchTerm) ||
                          option.userId.toString().includes(searchTerm) ||
                          (option.email && option.email.toLowerCase().includes(searchTerm))
                        );
                      }}
                      renderOption={(props, option) => {
                        const { key, ...otherProps } = props;
                        return (
                          <Box component="li" key={key} {...otherProps}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                              <Person color="primary" fontSize="small" />
                              <Typography variant="body1" fontWeight={600}>
                                {option.userName}
                              </Typography>
                             
                            </Box>
                          </Box>
                        );
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Customer ID or Name"
                          placeholder="Enter customer ID or select from list"
                          required
                          fullWidth
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <>
                                <InputAdornment position="start">
                                  <Business color={formData.userId ? 'primary' : 'disabled'} />
                                </InputAdornment>
                                {params.InputProps.startAdornment}
                              </>
                            )
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '&:hover fieldset': {
                                borderColor: accent,
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: accent,
                              }
                            }
                          }}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Customer Name"
                      value={formData.userName}
                      variant="outlined"
                      InputProps={{
                        readOnly: true,
                        startAdornment: (
                          <InputAdornment position="start">
                            <Person color={formData.userName ? 'success' : 'disabled'} />
                          </InputAdornment>
                        ),
                        endAdornment: formData.userName && (
                          <InputAdornment position="end">
                            <CheckCircle color="success" />
                          </InputAdornment>
                        )
                      }}
                      sx={{ 
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: formData.userName ? 'rgba(76, 175, 80, 0.08)' : 'grey.50'
                        }
                      }}
                    />
                  </Grid>

                  {formData.userName && (
                    <Grid item xs={12}>
                      <Alert 
                        severity="success" 
                        icon={<CheckCircle />}
                        sx={{ 
                          borderLeft: '4px solid',
                          borderColor: 'success.main'
                        }}
                      >
                        <strong>{formData.userName}</strong> is a verified customer on DPG Finance
                      </Alert>
                    </Grid>
                  )}
                </Grid>
              </Box>

              <Divider sx={{ my: 4 }} />

              {/* Limit Details */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ 
                  mb: 3, 
                  display: 'flex', 
                  alignItems: 'center',
                  fontWeight: 600,
                  color: 'text.primary'
                }}>
                  <Euro sx={{ mr: 1.5, color: accent }} />
                  DPG Limit Details
                </Typography>

                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Digital Payment Limit"
                      value={formData.amount}
                      onChange={(e) => handleAmountChange(e.target.value)}
                      variant="outlined"
                      required
                      type="text"
                      placeholder="0.00"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Typography variant="body1" sx={{ fontWeight: 600, color: formData.amount ? accent : 'text.disabled' }}>
                              EUR
                            </Typography>
                          </InputAdornment>
                        )
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': {
                            borderColor: accent,
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: accent,
                          }
                        },
                        '& input': {
                          fontSize: '1.1rem',
                          fontWeight: 500
                        }
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormControl 
                      fullWidth 
                      required
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': {
                            borderColor: accent,
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: accent,
                          }
                        }
                      }}
                    >
                      <InputLabel>Currency</InputLabel>
                      <Select
                        value={formData.currency}
                        onChange={(e) => handleInputChange('currency', e.target.value)}
                        label="Currency"
                        startAdornment={
                          <InputAdornment position="start">
                            <Euro color="primary" />
                          </InputAdornment>
                        }
                      >
                        <MenuItem value="USD">USD - US Dollar</MenuItem>
                        <MenuItem value="EUR">EUR - Euro</MenuItem>
                        <MenuItem value="GBP">GBP - British Pound</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      fullWidth
                      label="Deposit Account"
                      value={formData.depositAccountNumber}
                      onChange={(e) => handleInputChange('depositAccountNumber', e.target.value)}
                      variant="outlined"
                      required
                      disabled={depositAccounts.length === 0}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <AccountBalance color={depositAccounts.length > 0 ? 'primary' : 'disabled'} />
                          </InputAdornment>
                        )
                      }}
                      sx={{
                        width: '100%',
                        minWidth: '200px',
                        '& .MuiOutlinedInput-root': {
                          width: '100%',
                          minWidth: '200px',
                          '&:hover fieldset': {
                            borderColor: accent,
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: accent,
                          },
                          '& .MuiSelect-select': {
                            width: '100%',
                            minWidth: '200px'
                          }
                        },
                        '& .MuiInputBase-input': {
                          width: '100%',
                          minWidth: '200px'
                        }
                      }}
                    >
                      {depositAccounts.map((account) => (
                        <MenuItem key={account} value={account}>
                          Account #{account}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DatePicker
                        label="Validity Date"
                        value={formData.validityDate}
                        onChange={(newValue) => handleInputChange('validityDate', newValue)}
                        minDate={new Date(new Date().setDate(new Date().getDate() + 1))}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            required: true,
                            variant: 'outlined',
                            InputProps: {
                              startAdornment: (
                                <InputAdornment position="start">
                                  <CalendarToday color={formData.validityDate ? 'primary' : 'disabled'} />
                                </InputAdornment>
                              ),
                            },
                            sx: {
                              '& .MuiOutlinedInput-root': {
                                '&:hover fieldset': {
                                  borderColor: accent,
                                },
                                '&.Mui-focused fieldset': {
                                  borderColor: accent,
                                },
                              }
                            }
                          },
                          popper: {
                            sx: {
                              '& .MuiPaper-root': {
                                borderRadius: 3,
                                boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                              },
                              '& .MuiPickersDay-root': {
                                borderRadius: 2,
                                '&.Mui-selected': {
                                  backgroundColor: accent,
                                  '&:hover': {
                                    backgroundColor: accentLight,
                                  }
                                }
                              },
                              '& .MuiPickersCalendarHeader-root': {
                                '& .MuiPickersCalendarHeader-switchViewButton': {
                                  color: accent,
                                },
                                '& .MuiPickersArrowSwitcher-button': {
                                  color: accent,
                                }
                              }
                            }
                          }
                        }}
                      />
                    </LocalizationProvider>
                  </Grid>

                  {depositAccounts.length === 0 && formData.userId && (
                    <Grid item xs={12}>
                      <Alert 
                        severity="warning" 
                        action={
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<Add />}
                            onClick={() => createDefaultDepositAccount(parseInt(formData.userId))}
                            sx={{
                            background: accentGradient,
                            color: contrast,
                            '&:hover': {
                              background: accentGradientHover,
                            }
                            }}
                          >
                            Create Account
                          </Button>
                        }
                      >
                        No deposit account found for this customer
                      </Alert>
                    </Grid>
                  )}
                </Grid>
              </Box>

              {/* Info Banner */}
              <Paper sx={{ 
                p: 2.5, 
                mb: 4, 
                backgroundColor: softBg,
                borderRadius: 3,
                border: `1px solid ${outline}`
              }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <InfoOutlined sx={{ color: accent, mt: 0.5 }} />
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, color: accent }}>
                      Important Information
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      • Limit amount must be greater than zero<br/>
                      • Validity date must be a future date<br/>
                      • Only one active limit can be defined per customer<br/>
                      • A valid deposit account must be selected
                    </Typography>
                  </Box>
                </Box>
              </Paper>

              {/* Error/Success Messages */}
              {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
                  {error}
                </Alert>
              )}

              {success && (
                <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
                  {success}
                </Alert>
              )}

              {/* Action Buttons */}
              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <Button
                  variant="outlined"
                  onClick={handleReset}
                  disabled={loading}
                  startIcon={<Refresh />}
                  size="large"
                  sx={{
                    borderColor: accent,
                    color: accent,
                    '&:hover': {
                      borderColor: accentLight,
                      backgroundColor: softBg,
                    }
                  }}
                >
                  Reset
                </Button>

                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  endIcon={loading ? <CircularProgress size={20} color="inherit" /> : <ArrowForward />}
                  size="large"
                  sx={{ 
                    minWidth: 180,
                    background: accentGradient,
                    boxShadow: `0 4px 15px ${outline}`,
                    '&:hover': {
                      background: accentGradientHover,
                      boxShadow: `0 6px 20px ${outline}`,
                    }
                  }}
                >
                  {loading ? 'Defining...' : 'Define Limit'}
                </Button>
              </Stack>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default DefineLimit;